// Themes: Flame's 16 palettes, each expanded into a light and a dark variant
// so every theme follows the system colour scheme. Used by the server (CSS
// injected into index.html, so there's no flash of the wrong colours) and by
// the client (live preview when settings change).

export interface Palette {
  background: string;
  primary: string; // text
  accent: string;
}

// The original Flame themes. Each is native to one mode; the other is derived.
export const THEMES: Record<string, Palette> = {
  blackboard: { background: '#1a1a1a', primary: '#FFFDEA', accent: '#5c5c5c' },
  gazette: { background: '#F2F7FF', primary: '#000000', accent: '#5c5c5c' },
  espresso: { background: '#21211F', primary: '#D1B59A', accent: '#4E4E4E' },
  cab: { background: '#F6D305', primary: '#1F1F1F', accent: '#424242' },
  cloud: { background: '#f1f2f0', primary: '#35342f', accent: '#37bbe4' },
  lime: { background: '#263238', primary: '#AABBC3', accent: '#aeea00' },
  white: { background: '#ffffff', primary: '#222222', accent: '#dddddd' },
  tron: { background: '#242B33', primary: '#EFFBFF', accent: '#6EE2FF' },
  blues: { background: '#2B2C56', primary: '#EFF1FC', accent: '#6677EB' },
  passion: { background: '#f5f5f5', primary: '#12005e', accent: '#8e24aa' },
  chalk: { background: '#263238', primary: '#AABBC3', accent: '#FF869A' },
  paper: { background: '#F8F6F1', primary: '#4C432E', accent: '#AA9A73' },
  neon: { background: '#091833', primary: '#EFFBFF', accent: '#ea00d9' },
  pumpkin: { background: '#2d3436', primary: '#EFFBFF', accent: '#ffa500' },
  onedark: { background: '#282c34', primary: '#dfd9d6', accent: '#98c379' },
  mint: { background: '#282525', primary: '#d9d9d9', accent: '#50fbc2' },
};
export const DEFAULT_THEME = 'tron';

export type ColorMode = 'light' | 'dark';

export interface ThemeTokens {
  bg: string;
  surface: string;
  surface2: string;
  border: string;
  fg: string;
  muted: string;
  accent: string;
  accentFg: string;
  danger: string;
}

// ---------------------------------------------------------------- colour math

type RGB = [number, number, number];

function parseHex(hex: string): RGB {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? [...h].map((c) => c + c).join('') : h;
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) as RGB;
}

function toHex([r, g, b]: RGB): string {
  return `#${[r, g, b]
    .map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0'))
    .join('')}`;
}

/** Linear blend: t=0 gives a, t=1 gives b. */
export function mix(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  return toHex(ca.map((v, i) => v + (cb[i]! - v) * t) as RGB);
}

function luminance(hex: string): number {
  const [r, g, b] = parseHex(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as RGB;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio, 1–21. */
export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

const isDark = (hex: string) => luminance(hex) < 0.18;

/** Push `color` toward black or white until it reaches `min` contrast on `bg`. */
export function ensureContrast(color: string, bg: string, min: number): string {
  if (contrast(color, bg) >= min) return color;
  const target = isDark(bg) ? '#ffffff' : '#000000';
  for (let t = 0.05; t <= 1; t += 0.05) {
    const c = mix(color, target, t);
    if (contrast(c, bg) >= min) return c;
  }
  return target;
}

// ---------------------------------------------------------------- tokens

function tokensFrom(bg: string, fg: string, accent: string): ThemeTokens {
  fg = ensureContrast(fg, bg, 7);
  accent = ensureContrast(accent, bg, 3);
  return {
    bg,
    surface: mix(bg, fg, 0.05),
    surface2: mix(bg, fg, 0.1),
    border: mix(bg, fg, 0.16),
    fg,
    muted: ensureContrast(mix(fg, bg, 0.35), bg, 4.5),
    accent,
    accentFg: contrast('#000000', accent) >= contrast('#ffffff', accent) ? '#000000' : '#ffffff',
    danger: ensureContrast('#e5484d', bg, 4.5),
  };
}

export function themeTokens(
  name: string,
  mode: ColorMode,
  accentOverride: string | null = null
): ThemeTokens {
  const p = THEMES[name] ?? THEMES[DEFAULT_THEME]!;
  const accent = accentOverride ?? p.accent;
  const native: ColorMode = isDark(p.background) ? 'dark' : 'light';
  if (mode === native) return tokensFrom(p.background, p.primary, accent);
  if (mode === 'dark') {
    // A dark surface tinted with the accent; the light background becomes text.
    return tokensFrom(mix('#121418', accent, 0.06), p.background, accent);
  }
  return tokensFrom(mix('#fafafa', accent, 0.06), mix(p.background, '#000000', 0.3), accent);
}

const toCssVars = (t: ThemeTokens) =>
  `--bg:${t.bg};--surface:${t.surface};--surface-2:${t.surface2};--border:${t.border};` +
  `--fg:${t.fg};--muted:${t.muted};--accent:${t.accent};--accent-fg:${t.accentFg};--danger:${t.danger};`;

export interface AppearanceInput {
  mode: 'system' | 'light' | 'dark';
  theme: string;
  accent: string | null;
}

/** CSS custom properties for :root, following the system scheme when mode is "system". */
export function themeCss(a: AppearanceInput): string {
  const light = themeTokens(a.theme, 'light', a.accent);
  const dark = themeTokens(a.theme, 'dark', a.accent);
  if (a.mode === 'light') return `:root{color-scheme:light;${toCssVars(light)}}`;
  if (a.mode === 'dark') return `:root{color-scheme:dark;${toCssVars(dark)}}`;
  return (
    `:root{color-scheme:light dark;${toCssVars(light)}}` +
    `@media (prefers-color-scheme: dark){:root{${toCssVars(dark)}}}`
  );
}

/** Browser chrome colours for <meta name="theme-color" media=...>. */
export function themeColors(a: AppearanceInput): { light: string; dark: string } {
  const light = themeTokens(a.theme, 'light', a.accent).bg;
  const dark = themeTokens(a.theme, 'dark', a.accent).bg;
  if (a.mode === 'light') return { light, dark: light };
  if (a.mode === 'dark') return { light: dark, dark };
  return { light, dark };
}
